import { userType } from '../user/user.constants';
import { incentiveProgram } from '../project/project.constants';

export const ManagerialPermission = {
  biDashBoard: {
    code: 'biDashBoard',
    name: 'BI Dashboard'
  },
  entDashBoard: {
    code: 'entDashBoard',
    name: 'Entertainment Dashboard'
  },
  stepDashBoard: {
    code: 'stepDashBoard',
    name: 'STEP Dashboard'
  },
  rdDashBoard: {
    code: 'rdDashBoard',
    name: 'RD Dashboard'
  },
  search: {
    code: 'search',
    name: 'Search'
  },
  projectSummaryReadWrite: {
    code: 'projectSummaryReadWrite',
    name: 'Project Summary View'
  },
  formReview: {
    code: 'formReview',
    name: 'Form Review'
  },
  externalReview: {
    code: 'externalReview',
    name: 'External Review'
  },
  board: {
    code: 'board',
    name: 'Board'
  },
  signatures: {
    code: 'signatures',
    name: 'Signatures'
  },
  payments: {
    code: 'payments',
    name: 'Payments'
  },
  projectAccess: {
    code: 'projectAccess',
    name: 'Project Access'
  },
  userAccounts: {
    code: 'userAccounts',
    name: 'User Accounts'
  },
  createNewUser: {
    code: 'createNewUser',
    name: 'Create New User'
  }
};

export interface Permission {
  display: string;
  value: string;
  description?: string;
}

// Had to change from key [key: string]: Permission so that userTypeToPermissionsMap works
export interface Permissions {
  canPerformActions: Permission;
  canViewConfidentialProjects: Permission;
  canPerformEzActions: Permission;
  canPerformQjActions: Permission;
  canPerformRtaActions: Permission;
  canPerformIteActions: Permission;
  canPerformFilmActions: Permission;
  canPerformDigitalActions: Permission;
  canPerformStepActions: Permission;
  canPerformRdActions: Permission;
  rdAssistantDirector: Permission;
  rdDirector: Permission;
  canResetTwoFactor: Permission;
}

export interface UserTypeToPermission {
  [key: string]: Permission[];
}

export const permissions: Permissions = {
  canPerformActions: <Permission>{
    display: 'Can Perform Actions',
    value: 'canPerformActions',
    description: 'Allows this reviewer to take actions on assigned forms (such as submitting reviews and completing assignments) rather than only viewing them read-only.'
  },
  canViewConfidentialProjects: <Permission>{
    display: 'Can View Confidential Projects',
    value: 'canViewConfidentialProjects',
    description: 'Allows this user to open and review projects that have been flagged as confidential.'
  },
  canPerformEzActions: <Permission> {
    display: 'EZ Actions',
    value: 'canPerformEzActions',
    description: 'Grants the ability to perform management and review actions on Enterprise Zone (EZ) forms.'
  },
  canPerformQjActions: <Permission> {
    display: 'QJ Actions',
    value: 'canPerformQjActions',
    description: 'Grants the ability to perform management and review actions on Quality Jobs (QJ) forms.'
  },
  canPerformRtaActions: <Permission> {
    display: 'RTA Actions',
    value: 'canPerformRtaActions',
    description: 'Grants the ability to perform management and review actions on Restoration Tax Abatement (RTA) forms.'
  },
  canPerformIteActions: <Permission> {
    display: 'ITE Actions',
    value: 'canPerformIteActions',
    description: 'Grants the ability to perform management and review actions on Industrial Tax Exemption (ITE) forms.'
  },
  canPerformFilmActions: <Permission> {
    display: 'Film Actions',
    value: 'canPerformFilmActions',
    description: 'Grants the ability to perform management and review actions on Motion Picture (Film) forms.'
  },
  canPerformDigitalActions: <Permission> {
    display: 'Digital Actions',
    value: 'canPerformDigitalActions',
    description: 'Grants the ability to perform management and review actions on Digital Media forms.'
  },
  canPerformStepActions: <Permission> {
    display: 'STEP Actions',
    value: 'canPerformStepActions',
    description: 'Grants the ability to perform management and review actions on State Trade Expansion Program (STEP) forms.'
  },
  canPerformRdActions: <Permission> {
    display: 'RD Actions',
    value: 'canPerformRdActions',
    description: 'Grants the ability to perform management and review actions on Research and Development (R&D) forms.'
  },
  rdAssistantDirector: <Permission> {
    display: 'RD Review (AD)',
    value: 'rdAssistantDirector',
    description: 'Designates this user as the R&D Assistant Director reviewer, allowing them to complete the Assistant Director review step in the R&D approval workflow.'
  },
  rdDirector: <Permission> {
    display: 'RD Review (Dir)',
    value: 'rdDirector',
    description: 'Designates this user as the R&D Director reviewer, allowing them to complete the final Director review step in the R&D approval workflow.'
  },
  canResetTwoFactor: <Permission>{
    display: 'Reset Two-Factor Authentication',
    value: 'canResetTwoFactor',
    description: 'Allows this user to reset the two-factor authentication (2FA) enrollment for other user accounts.'
  }
};

/**
 *
 */
export const programActionPermissions = {
  [incentiveProgram.ez.code]: permissions.canPerformEzActions.value,
  [incentiveProgram.qj.code]: permissions.canPerformQjActions.value,
  [incentiveProgram.rta.code]: permissions.canPerformRtaActions.value,
  [incentiveProgram.ite.code]: permissions.canPerformIteActions.value,
  [incentiveProgram.film.code]: permissions.canPerformFilmActions.value,
  [incentiveProgram.dm.code]: permissions.canPerformDigitalActions.value,
  [incentiveProgram.step.code]: permissions.canPerformStepActions.value,
  [incentiveProgram.rd.code]: permissions.canPerformRdActions.value,

}

export const userTypeToPermissionsMap: UserTypeToPermission = {
  [userType.management.code]: [
    permissions.canPerformEzActions,
    permissions.canPerformQjActions,
    permissions.canPerformRtaActions,
    permissions.canPerformIteActions,
    permissions.canPerformFilmActions,
    permissions.canPerformDigitalActions,
    permissions.canPerformStepActions,
    permissions.canPerformRdActions,
    permissions.rdAssistantDirector,
    permissions.rdDirector,
    permissions.canResetTwoFactor
  ],
  [userType.applicant.code]: [],
  [userType.assessor.code]: [
    permissions.canPerformActions,
    permissions.canViewConfidentialProjects
  ],
  [userType.external.code]: [
    permissions.canPerformActions,
    permissions.canViewConfidentialProjects
  ],
  [userType.board.code]: []
};
